import { BaseModel } from './base';

import type { Permissions } from '../types/interfaces/permissions';

// Enums
export enum ServiceAssociationOrderType {
  After = 0,
  Before = 1,
}

export const ServiceAssociationOrderTypeLabels: Record<ServiceAssociationOrderType, string> = {
  [ServiceAssociationOrderType.After]: 'components.service.actions.editAddOn.after',
  [ServiceAssociationOrderType.Before]: 'components.service.actions.editAddOn.before',
};

export enum ServiceAssociationDiscountType {
  Fixed = 0,
  Percentage = 1,
}

export const ServiceAssociationDiscountTypeLabels: Record<ServiceAssociationDiscountType, string> = {
  [ServiceAssociationDiscountType.Fixed]: 'general.fixed',
  [ServiceAssociationDiscountType.Percentage]: 'general.percentage',
};

export class ServiceAssociation extends BaseModel {
  id!: number;
  idAssociation!: number;
  idService!: number;
  idServiceAssociated!: number;
  target_type!: 'service' | 'product';
  target_id!: number;
  type!: number;
  orderType!: ServiceAssociationOrderType;
  discountType!: ServiceAssociationDiscountType;
  discountAmount!: number;
  deleted!: number;
  created!: number;
  modified!: number;
  permissions?: Permissions;

  // Associated service details (populated from API)
  title?: string;
  duration?: number;
  price?: number;

  override objectID(): string {
    return this.idAssociation?.toString() || '';
  }

  static formatDiscountedPrice(association: any, currencySymbol: string): string {
    if (!association?.target || Number(association.discountAmount) <= 0) {
      return '';
    }

    const discountAmount = Number(association.discountAmount || 0);
    const basePrice = Number(association.target.price || 0);
    const discountType = association.discountType;
    const format = (amount: number) => currencySymbol + Number(amount || 0).toFixed(2);

    if (discountType === ServiceAssociationDiscountType.Fixed || discountType === 0) {
      return format(basePrice - discountAmount);
    }

    if (discountType === ServiceAssociationDiscountType.Percentage || discountType === 1) {
      return format(basePrice - (basePrice * discountAmount) / 100);
    }

    return '';
  }

  get isOrderTypeAfter(): boolean {
    return this.orderType === ServiceAssociationOrderType.After;
  }

  get isOrderTypeBefore(): boolean {
    return this.orderType === ServiceAssociationOrderType.Before;
  }

  get isDiscountTypeFixed(): boolean {
    return this.discountType === ServiceAssociationDiscountType.Fixed;
  }

  get isDiscountTypePercentage(): boolean {
    return this.discountType === ServiceAssociationDiscountType.Percentage;
  }

  get orderTypeLabel(): string {
    return ServiceAssociationOrderTypeLabels[this.orderType] || '';
  }

  get discountTypeLabel(): string {
    return ServiceAssociationDiscountTypeLabels[this.discountType] || '';
  }

  override toJSON(): Record<string, any> {
    return {
      orderType: this.orderType,
      discountType: this.discountType,
      discountAmount: this.discountAmount ?? 0,
    };
  }
}
